import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../Utils/Axios';
import UrduKeyboard from '../../Components/UrduKeyboard';

function RecoveriesTab({ recoveries, ledgerParties, companyLanguage, handleEditRecovery, handleDeleteRecovery }) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchUrduKeyboard, setShowSearchUrduKeyboard] = useState(false);
  const searchInputRef = useRef(null);
  // Set today's date (local) as default for date filters
  const today = new Date().toLocaleDateString('en-CA');
  const [filterDateFrom, setFilterDateFrom] = useState(today);
  const [filterDateTo, setFilterDateTo] = useState(today);
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterPaymentMethod, setFilterPaymentMethod] = useState('');
  const [filterAmountMin, setFilterAmountMin] = useState('');
  const [filterAmountMax, setFilterAmountMax] = useState('');
  const [showFilters, setShowFilters] = useState(true); // Auto-open filters by default
  const [selectedRecoveries, setSelectedRecoveries] = useState([]);
  const [showBalance, setShowBalance] = useState(false);

  // Get unique customers and payment methods for filter dropdowns
  const uniqueCustomers = useMemo(() => {
    const customers = new Set();
    recoveries.forEach(recovery => {
      const name = recovery.customerNameUrdu || recovery.customerName;
      if (name) customers.add(name);
    });
    return Array.from(customers).sort();
  }, [recoveries]);

  const uniquePaymentMethods = useMemo(() => {
    const methods = new Set();
    recoveries.forEach(recovery => {
      if (recovery.paymentMethod) methods.add(recovery.paymentMethod);
    });
    return Array.from(methods).sort();
  }, [recoveries]);

  // Filter recoveries based on all filter criteria
  const filteredRecoveries = useMemo(() => {
    return recoveries.filter(recovery => {
      // Search term filter (searches in customer name, serial number, description)
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const customerName = (recovery.customerNameUrdu || recovery.customerName || '').toLowerCase();
        const serialNumber = (recovery.serialNumber || '').toString().toLowerCase();
        const description = (recovery.descriptionUrdu || recovery.description || '').toLowerCase();

        if (!customerName.includes(searchLower) &&
          !serialNumber.includes(searchLower) &&
          !description.includes(searchLower)) {
          return false;
        }
      }

      // Date range filter
      if (filterDateFrom && recovery.date) {
        const recoveryDate = new Date(recovery.date);
        const fromDate = new Date(filterDateFrom);
        // Compare dates by zeroing out time components in local time
        recoveryDate.setHours(0, 0, 0, 0);
        fromDate.setHours(0, 0, 0, 0);
        if (recoveryDate < fromDate) return false;
      }
      if (filterDateTo && recovery.date) {
        const recoveryDate = new Date(recovery.date);
        const toDate = new Date(filterDateTo);
        // Compare dates by zeroing out time components in local time
        recoveryDate.setHours(0, 0, 0, 0);
        toDate.setHours(0, 0, 0, 0);
        if (recoveryDate > toDate) return false;
      }

      // Customer filter
      if (filterCustomer) {
        const customerName = recovery.customerNameUrdu || recovery.customerName || '';
        if (customerName !== filterCustomer) return false;
      }

      // Payment method filter
      if (filterPaymentMethod) {
        if (recovery.paymentMethod !== filterPaymentMethod) return false;
      }

      // Amount range filter
      const amount = recovery.recoveryAmount || 0;
      if (filterAmountMin) {
        if (amount < parseFloat(filterAmountMin)) return false;
      }
      if (filterAmountMax) {
        if (amount > parseFloat(filterAmountMax)) return false;
      }

      return true;
    });
  }, [recoveries, searchTerm, filterDateFrom, filterDateTo, filterCustomer, filterPaymentMethod, filterAmountMin, filterAmountMax]);

  // Handle Urdu keyboard input for search field
  const handleSearchUrduKeyPress = (key) => {
    if (key === 'backspace') {
      setSearchTerm((prev) => prev ? prev.slice(0, -1) : '');
    } else {
      setSearchTerm((prev) => (prev || '') + key);
    }
  };

  // Auto-close Urdu keyboard when English character is typed in searchTerm
  // Auto-open when field becomes empty
  useEffect(() => {
    if (searchTerm === undefined || searchTerm === null) return;

    const value = String(searchTerm);

    // Check if field contains any English characters
    const hasEnglishChar = /[a-zA-Z]/.test(value);

    if (hasEnglishChar) {
      // Close Urdu keyboard if English character is found
      setShowSearchUrduKeyboard(false);
    } else if (value.trim() === '' && showSearchUrduKeyboard === false) {
      // Open Urdu keyboard if field becomes empty and keyboard is not already open
      // Only open if the field is focused (to avoid opening when form is reset)
      if (searchInputRef.current && searchInputRef.current === document.activeElement) {
        setShowSearchUrduKeyboard(true);
      }
    }
  }, [searchTerm, showSearchUrduKeyboard]);

  // Toggle Urdu keyboard on search input click
  // Open keyboard if field is empty, otherwise toggle
  const handleSearchInputClick = () => {
    if (!searchTerm || searchTerm.trim() === '') {
      // If field is empty, open keyboard
      setShowSearchUrduKeyboard(true);
    } else {
      // Otherwise toggle
      setShowSearchUrduKeyboard(prev => !prev);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterCustomer('');
    setFilterPaymentMethod('');
    setFilterAmountMin('');
    setFilterAmountMax('');
  };

  // Check if any filter is active
  const hasActiveFilters = searchTerm || filterDateFrom || filterDateTo || filterCustomer || filterPaymentMethod || filterAmountMin || filterAmountMax;

  // Select All functionality
  const handleSelectAll = () => {
    const allFilteredIds = filteredRecoveries.map(recovery => recovery._id);
    const allSelected = allFilteredIds.every(id => selectedRecoveries.includes(id));

    if (allSelected) {
      // Deselect all filtered recoveries
      setSelectedRecoveries(prev => prev.filter(id => !allFilteredIds.includes(id)));
    } else {
      // Select all filtered recoveries
      setSelectedRecoveries(prev => {
        const newSelection = [...prev];
        allFilteredIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  const handleToggleRecovery = (recoveryId) => {
    setSelectedRecoveries(prev => {
      if (prev.includes(recoveryId)) {
        return prev.filter(id => id !== recoveryId);
      } else {
        return [...prev, recoveryId];
      }
    });
  };

  const isAllSelected = filteredRecoveries.length > 0 && filteredRecoveries.every(recovery => selectedRecoveries.includes(recovery._id));

  // Bulk Delete functionality
  const handleBulkDelete = async () => {
    if (selectedRecoveries.length === 0) return;

    const confirmMessage = companyLanguage === 'ur'
      ? `کیا آپ واقعی ${selectedRecoveries.length} ریکوریز ختم کرنا چاہتے ہیں؟`
      : `Are you sure you want to delete ${selectedRecoveries.length} recoveries?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      const token = localStorage.getItem('companyToken');
      if (!token) {
        toast.error(companyLanguage === 'ur' ? 'لاگ ان کریں' : 'Please login');
        return;
      }

      let successCount = 0;
      let failCount = 0;

      // Delete all selected recoveries in parallel
      const deletePromises = selectedRecoveries.map(async (recoveryId) => {
        try {
          await api.delete(`/recovery/company/${recoveryId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          return { success: true, recoveryId };
        } catch (error) {
          console.error(`Failed to delete recovery ${recoveryId}:`, error);
          return { success: false, recoveryId };
        }
      });

      const results = await Promise.all(deletePromises);
      successCount = results.filter(r => r.success).length;
      failCount = results.filter(r => !r.success).length;

      setSelectedRecoveries([]);

      if (successCount > 0) {
        toast.success(
          companyLanguage === 'ur'
            ? `${successCount} ریکوریز کامیابی سے ختم ہو گئیں`
            : `${successCount} recoveries deleted successfully`
        );
        // Refresh recoveries list
        if (window.location.reload) {
          setTimeout(() => window.location.reload(), 500);
        }
      }

      if (failCount > 0) {
        toast.error(
          companyLanguage === 'ur'
            ? `${failCount} ریکوریز ختم کرنے میں ناکام`
            : `Failed to delete ${failCount} recoveries`
        );
      }
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error(companyLanguage === 'ur' ? 'ختم کرنے میں ناکام' : 'Failed to delete recoveries');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 border border-brand-card animate-fade-in-up">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h2 className={`text-2xl font-bold text-brand-header ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
          {companyLanguage === 'ur' ? 'ریکوریز' : 'Recoveries'}
          <span className="text-lg text-gray-500 ml-2">
            ({filteredRecoveries.length}/{recoveries.length})
          </span>
        </h2>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 transform hover:scale-105 ${showFilters || hasActiveFilters
              ? 'bg-green-500 hover:bg-green-600 text-white'
              : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {companyLanguage === 'ur' ? 'فلٹر' : 'Filter'}
            {hasActiveFilters && (
              <span className="bg-white text-green-500 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                !
              </span>
            )}
          </button>
          <button
            onClick={() => setShowBalance(!showBalance)}
            className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 transform hover:scale-105 ${showBalance
              ? 'bg-orange-500 hover:bg-orange-600 text-white'
              : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {companyLanguage === 'ur' ? (showBalance ? 'بیلنس چھپائیں' : 'بیلنس دکھائیں') : (showBalance ? 'Hide Balance' : 'Show Balance')}
          </button>
          <button
            onClick={() => navigate('/recovery/create')}
            className="bg-brand-header hover:bg-emerald-800 text-white font-semibold px-4 py-2 rounded-lg transition-all flex items-center gap-2 transform hover:scale-105 hover:shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {companyLanguage === 'ur' ? 'نیا ریکوری' : 'Create New Recovery'}
          </button>
          <button
            onClick={() => navigate('/recovery/records')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg transition-all flex items-center gap-2 transform hover:scale-105 hover:shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {companyLanguage === 'ur' ? 'ریکوری ریکارڈ' : 'Recovery Records'}
          </button>
        </div>
      </div>

      {/* Filter Section */}
      {showFilters && (
        <div className="mb-2 p-1.5 bg-gradient-to-r from-green-50 to-lime-50 rounded-xl border border-green-200 animate-fade-in-down shadow-lg">
          <div className="flex justify-between items-center mb-1">
            <h3 className={`font-bold text-green-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
              {companyLanguage === 'ur' ? 'فلٹرز' : 'Filters'}
            </h3>
            <div className="flex gap-2">
              {hasActiveFilters && (
                <button
                  onClick={resetFilters}
                  className="px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white text-xs rounded-lg transition-all"
                >
                  {companyLanguage === 'ur' ? 'ری سیٹ' : 'Reset'}
                </button>
              )}
              <button
                onClick={() => setShowFilters(false)}
                className="px-2 py-1 bg-gray-300 hover:bg-gray-400 text-gray-700 text-xs rounded-lg transition-all"
              >
                {companyLanguage === 'ur' ? 'بند کریں' : 'Close'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-1.5">
            {/* Search */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'تلاش:' : 'Search:'}
              </label>
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={handleSearchInputClick}
                onFocus={() => {
                  // Open Urdu keyboard on focus if field is empty
                  if (!searchTerm || searchTerm.trim() === '') {
                    setShowSearchUrduKeyboard(true);
                  }
                }}
                placeholder={companyLanguage === 'ur' ? 'کسٹمر، نمبر، تفصیل...' : 'Customer, Number, Description...'}
                className={`w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs ${companyLanguage === 'ur' ? 'font-urdu text-right' : ''}`}
                style={companyLanguage === 'ur' ? {
                  fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif",
                  lineHeight: '1.8',
                  paddingTop: '6px',
                  paddingBottom: '6px',
                  fontSize: '12px',
                  height: 'auto',
                  minHeight: '32px',
                  display: 'block',
                  overflow: 'visible',
                  verticalAlign: 'middle'
                } : {}}
                dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}
                lang={companyLanguage === 'ur' ? 'ur' : 'en'}
              />
            </div>

            {/* Date From */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'تاریخ سے:' : 'Date From:'}
              </label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
              />
            </div>

            {/* Date To */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'تاریخ تک:' : 'Date To:'}
              </label>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
              />
            </div>

            {/* Customer Filter */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'کسٹمر:' : 'Customer:'}
              </label>
              <select
                value={filterCustomer}
                onChange={(e) => setFilterCustomer(e.target.value)}
                className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
              >
                <option value="">{companyLanguage === 'ur' ? 'تمام' : 'All'}</option>
                {uniqueCustomers.map(customer => (
                  <option key={customer} value={customer}>{customer}</option>
                ))}
              </select>
            </div>

            {/* Payment Method Filter */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'ادائیگی کا طریقہ:' : 'Payment Method:'}
              </label>
              <select
                value={filterPaymentMethod}
                onChange={(e) => setFilterPaymentMethod(e.target.value)}
                className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
              >
                <option value="">{companyLanguage === 'ur' ? 'تمام' : 'All'}</option>
                {uniquePaymentMethods.map(method => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </select>
            </div>

            {/* Amount Min */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'کم از کم رقم:' : 'Min Amount:'}
              </label>
              <input
                type="number"
                value={filterAmountMin}
                onChange={(e) => setFilterAmountMin(e.target.value)}
                placeholder="0"
                className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
              />
            </div>

            {/* Amount Max */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'زیادہ سے زیادہ رقم:' : 'Max Amount:'}
              </label>
              <input
                type="number"
                value={filterAmountMax}
                onChange={(e) => setFilterAmountMax(e.target.value)}
                placeholder="∞"
                className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
              />
            </div>
          </div>
        </div>
      )}

      {recoveries.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>{companyLanguage === 'ur' ? 'ابھی کوئی ریکوری نہیں' : 'No recoveries found yet'}</p>
          <p className="text-sm mt-2">
            {companyLanguage === 'ur' ? 'نیا ریکوری بنانے کے لیے اوپر بٹن دبائیں' : 'Use the button above to create a new recovery'}
          </p>
        </div>
      ) : filteredRecoveries.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>{companyLanguage === 'ur' ? 'فلٹر کے مطابق کوئی ریکوری نہیں ملی' : 'No recoveries match the filters'}</p>
          <button
            onClick={resetFilters}
            className="mt-4 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-all"
          >
            {companyLanguage === 'ur' ? 'فلٹرز ری سیٹ کریں' : 'Reset Filters'}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Header Row */}
          {filteredRecoveries.length > 0 && (
            <div className="bg-gray-100 border border-brand-card rounded-lg py-2 px-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    className="w-5 h-5 text-brand-header border-gray-300 rounded focus:ring-brand-header cursor-pointer"
                  />
                  <span className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                    {companyLanguage === 'ur' ? 'سب منتخب' : 'Select All'}
                  </span>
                  {selectedRecoveries.length > 0 && (
                    <span className={`text-sm text-gray-600 ml-2 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                      ({selectedRecoveries.length} {companyLanguage === 'ur' ? 'منتخب' : 'selected'})
                    </span>
                  )}
                </div>
                {selectedRecoveries.length > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    className={`px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-all flex items-center gap-2 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {companyLanguage === 'ur' ? `ختم کریں (${selectedRecoveries.length})` : `Delete (${selectedRecoveries.length})`}
                  </button>
                )}
              </div>
              <div className={`grid ${showBalance ? 'grid-cols-8' : 'grid-cols-7'} gap-4 items-center`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {companyLanguage === 'ur' ? 'نمبر شمار' : 'Sr. No.'}
                </div>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {companyLanguage === 'ur' ? 'منتخب' : 'Select'}
                </div>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {companyLanguage === 'ur' ? 'تاریخ' : 'Date'}
                </div>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {companyLanguage === 'ur' ? 'کسٹمر' : 'Customer'}
                </div>
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {companyLanguage === 'ur' ? 'وصولی رقم' : 'Recovery Amount'}
                </div>
                {showBalance && (
                  <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                    {companyLanguage === 'ur' ? 'بقایا رقم' : 'Balance'}
                  </div>
                )}
                <div className={`text-sm font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {companyLanguage === 'ur' ? 'ادائیگی کا طریقہ' : 'Payment Method'}
                </div>
                <div className={`text-sm font-bold text-gray-700 text-center`}>
                  {companyLanguage === 'ur' ? 'عمل' : 'Actions'}
                </div>
              </div>
            </div>
          )}

          {/* Recovery Items */}
          {filteredRecoveries.map((recovery, index) => {
            const isSelected = selectedRecoveries.includes(recovery._id);
            // Find customer balance
            const ledgerParty = ledgerParties && ledgerParties.find(p => p._id === recovery.customerPartyId);
            const currentBalance = ledgerParty ? (ledgerParty.currentBalance || 0) : 0;

            return (
              <div
                key={recovery._id}
                className={`border border-brand-card rounded-lg py-2 px-3 hover:shadow-md transition-all ${isSelected ? 'bg-emerald-50 border-green-400' : 'bg-white'
                  }`}
              >
                <div className={`grid ${showBalance ? 'grid-cols-8' : 'grid-cols-7'} gap-4 items-center`} dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                  <div className={`font-semibold text-gray-900 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                    {index + 1}
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggleRecovery(recovery._id)}
                      className="w-5 h-5 text-brand-header border-gray-300 rounded focus:ring-brand-header cursor-pointer"
                    />
                  </div>
                  <div className={`text-sm text-gray-600 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                    {new Date(recovery.date).toLocaleDateString()}
                  </div>
                  <div className={`text-sm text-gray-600 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                    {recovery.customerNameUrdu || recovery.customerName || 'N/A'}
                  </div>
                  <div className={`text-sm font-semibold text-green-600 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                    {(recovery.recoveryAmount || 0).toLocaleString()}
                  </div>
                  {showBalance && (
                    <div className={`text-sm font-semibold text-orange-600 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                      {currentBalance.toLocaleString()}
                    </div>
                  )}
                  <div className={`text-sm text-gray-600 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                    {recovery.paymentMethod || '-'}
                  </div>
                  <div className="flex gap-2 justify-center">
                    <button
                      onClick={() => navigate(`/recovery/view/${recovery._id}`)}
                      className={`px-2 py-1 bg-brand-header hover:bg-emerald-800 text-white text-xs rounded transition-all ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                    >
                      {companyLanguage === 'ur' ? 'دیکھیں' : 'View'}
                    </button>
                    <button
                      onClick={() => handleEditRecovery(recovery)}
                      className={`px-2 py-1 bg-brand-button hover:bg-yellow-400 text-gray-900 text-xs rounded transition-all ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                    >
                      {companyLanguage === 'ur' ? 'تبدیل کریں' : 'Edit'}
                    </button>
                    <button
                      onClick={() => handleDeleteRecovery(recovery._id)}
                      className={`px-2 py-1 bg-brand-alert hover:bg-red-600 text-white text-xs rounded transition-all ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                    >
                      {companyLanguage === 'ur' ? 'ختم کریں' : 'Delete'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <UrduKeyboard
        isVisible={showSearchUrduKeyboard}
        onKeyPress={handleSearchUrduKeyPress}
        onClose={() => setShowSearchUrduKeyboard(false)}
      />
    </div>
  );
}

export default RecoveriesTab;


